import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { getUserToken, isExpiredToken, loadUserFromJwtString, saveUserToken } from '@src/shared/providers/UserProvider'
import type { IcebergUser } from '@src/shared/types/IcebergUser'

interface UserContextValue {
  user: IcebergUser | null
  needAuth: boolean
  setToken: (jwt: string) => void
  clearToken: () => void
}

const UserContext = createContext<UserContextValue | null>(null)

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [jwtToken, setJwtToken] = useState(() => getUserToken())

  const user = useMemo(() => {
    if (!jwtToken) return null
    try {
      return loadUserFromJwtString(jwtToken)
    } catch {
      return null
    }
  }, [jwtToken])

  const needAuth = !user || isExpiredToken(user.exp)

  const setToken = (jwt: string) => {
    saveUserToken(jwt)
    setJwtToken(jwt)
  }

  const clearToken = () => {
    localStorage.removeItem('jwtToken')
    setJwtToken('')
  }

  const value = useMemo(
    () => ({ user, needAuth, setToken, clearToken }),
    [user, needAuth],
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}


export function useUser(): UserContextValue {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUser must be used within a UserProvider')
  return ctx
}
